import type { OrganisationTree } from 'api/organisations';

export type OrganisationSearchHit = {
  key: string;
  kind: 'organisation' | 'company' | 'farm';
  label: string;
  /** Optional secondary line for users — never organisation names. */
  detail?: string;
  orgId: number;
  companyId?: number;
  farmId?: number;
};

const normalize = (value: string | number | null | undefined) =>
  String(value ?? '')
    .trim()
    .toLowerCase();

export const normalizeOrganisationSearchQuery = (query: string) => normalize(query);

const tokenizeQuery = (query: string) =>
  normalize(query)
    .split(/\s+/)
    .filter(Boolean);

const matchesAllTokens = (haystack: string, tokens: string[]) =>
  tokens.every((token) => haystack.includes(token));

const buildHaystack = (...parts: Array<string | number | null | undefined>) =>
  parts.map((part) => normalize(part)).filter(Boolean).join(' ');

export function searchOrganisationTree(
  organisations: OrganisationTree[],
  rawQuery: string,
): { hits: OrganisationSearchHit[]; organisationIdsWithMatch: Set<number> } {
  const tokens = tokenizeQuery(rawQuery);
  const hits: OrganisationSearchHit[] = [];
  const organisationIdsWithMatch = new Set<number>();

  if (!tokens.length) {
    return { hits, organisationIdsWithMatch };
  }

  for (const org of organisations) {
    // Do not index organisation display names — users only see customer numbers.
    const orgHaystack = buildHaystack(org.id, org.customerNumber);

    if (matchesAllTokens(orgHaystack, tokens)) {
      organisationIdsWithMatch.add(org.id);
    }

    for (const farm of org.farms ?? []) {
      if (farm.active === false) {
        continue;
      }
      const farmLabel = String(farm.farmName || '').trim();
      // Search by farm name only — do not surface organisation names to users.
      const farmHaystack = buildHaystack(farmLabel);

      if (matchesAllTokens(farmHaystack, tokens)) {
        organisationIdsWithMatch.add(org.id);
        hits.push({
          key: `farm-${org.id}-0-${farm.id}`,
          kind: 'farm',
          label: farmLabel,
          orgId: org.id,
          farmId: farm.id,
        });
      }
    }

    for (const company of org.companies ?? []) {
      const companyName = String(company.name || '').trim();
      const companyRegistration = String(company.companyRegistration || '').trim();
      const companyHaystack = buildHaystack(companyName, companyRegistration);

      if (matchesAllTokens(companyHaystack, tokens)) {
        organisationIdsWithMatch.add(org.id);
        hits.push({
          key: `company-${org.id}-${company.id}`,
          kind: 'company',
          label: companyName,
          detail: companyRegistration || undefined,
          orgId: org.id,
          companyId: company.id,
        });
      }

      for (const farm of company.farms ?? []) {
        if (farm.active === false) {
          continue;
        }
        const farmLabel = String(farm.farmName || '').trim();
        const farmHaystack = buildHaystack(farmLabel);

        if (matchesAllTokens(farmHaystack, tokens)) {
          organisationIdsWithMatch.add(org.id);
          hits.push({
            key: `farm-${org.id}-${company.id}-${farm.id}`,
            kind: 'farm',
            label: farmLabel,
            orgId: org.id,
            companyId: company.id,
            farmId: farm.id,
          });
        }
      }
    }
  }

  const kindOrder = { farm: 0, company: 1, organisation: 2 } as const;
  hits.sort((left, right) => {
    const kindDiff = kindOrder[left.kind] - kindOrder[right.kind];
    if (kindDiff !== 0) {
      return kindDiff;
    }
    return left.label.localeCompare(right.label, 'sv', { sensitivity: 'base' });
  });

  return { hits, organisationIdsWithMatch };
}

export function filterOrganisationsForSearch(
  organisations: OrganisationTree[],
  organisationIdsWithMatch: Set<number>,
  hasQuery: boolean,
) {
  if (!hasQuery) {
    return organisations;
  }

  return organisations.filter((org) => organisationIdsWithMatch.has(org.id));
}

export function matchesOrganisationSearchTokens(
  rawQuery: string,
  ...parts: Array<string | number | null | undefined>
) {
  const tokens = tokenizeQuery(rawQuery);
  if (!tokens.length) {
    return true;
  }
  return matchesAllTokens(buildHaystack(...parts), tokens);
}
